import { useState, useEffect } from "react";
import Checklist from "../components/Tasks/Board/Checklist";
import { db } from "../components/Auth/firebase";
import { extractPriority } from "../utils/Boardutils";
import ReactMarkdown from "react-markdown";
import gfm from "remark-gfm";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Modal from "../components/Tasks/Board/Modal";
import { Exclaim } from "../components/Tasks/Board/Icons";
import {
  doc,
  updateDoc,
  deleteDoc,
  arrayRemove,
  getDocs,
  collection,
  query,
  where,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { useProjects } from "../context/ProjectsContext";
import AssigneeSelector from "./AssigneeSelector";
import emailjs from "emailjs-com";

const TaskDetails = ({
  taskDetails,
  boardId,
  userId,
  columnDetails,
  closeModal,
}) => {
  const [updatedTitle, setTitle] = useState(taskDetails.title);
  const [updatedPriority, setPriority] = useState(taskDetails.priority);
  const [updatedDesc, setNewDesc] = useState(taskDetails.description);
  const [modal, setModal] = useState(false);
  const [dueDate, setDueDate] = useState(taskDetails.dueDate?.toDate() || null);
  const [editing, setEditing] = useState(false);
  const [users, setUsers] = useState([]); // State to hold user list
  const [selectedAssignees, setSelectedAssignees] = useState(
    taskDetails.assignees || []
  ); // State to hold selected assignees
  const { activeProject } = useProjects();
  const [sentEmails, setSentEmails] = useState([]); // State to store sent emails

  useEffect(() => {
    // Fetch users from Firestore who are part of the active project
    const fetchUsers = async () => {
      if (activeProject && activeProject.assignees) {
        const assigneeUids = activeProject.assignees; // Array of user UIDs
        const usersCollection = collection(db, "users");
        const usersQuery = query(
          usersCollection,
          where("uid", "in", assigneeUids)
        );
        const usersSnapshot = await getDocs(usersQuery);
        const usersList = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(usersList);
      }
    };

    fetchUsers();
  }, [activeProject]);

  const sendEmail = async () => {
    try {
      const taskRef = doc(
        db,
        `projects/${activeProject.id}/boards/${boardId}/tasks`,
        taskDetails.id
      );
      const taskSnapshot = await getDoc(taskRef);
      if (taskSnapshot.exists()) {
        const taskData = taskSnapshot.data();

        selectedAssignees.forEach(async (assigneeId) => {
          const assignee = users.find((user) => user.uid === assigneeId);
          if (assignee && assignee.email) {
            // Check if email has already been sent to this assignee
            if (!taskData.sentEmails || !taskData.sentEmails[assigneeId]) {
              // Fetch the board name
              const boardRef = doc(
                db,
                `projects/${activeProject.id}/boards`,
                boardId
              );
              const boardSnapshot = await getDoc(boardRef);
              const boardData = boardSnapshot.data();

              const templateParams = {
                to_email: assignee.email,
                from_name: "CollabPro",
                task_name: taskDetails.title,
                project_name: activeProject.name,
                board_name: boardData ? boardData.name : "Your Board Name",
              };

              try {
                await emailjs.send(
                  "service_6tl10be", // Your service ID
                  "template_h5tm17t", // Your template ID
                  templateParams,
                  "guC4PSxaEdtDCHTTS" // Your user ID
                );
                console.log("Email sent successfully to", assignee.email);
                // Add the assignee to the list of sent emails
                await updateDoc(taskRef, {
                  [`sentEmails.${assigneeId}`]: true,
                });
              } catch (error) {
                console.error(
                  "Email sending failed to",
                  assignee.email,
                  ":",
                  error
                );
              }
            }
          } else {
            console.error(
              "Assignee not found or email not available for id:",
              assigneeId
            );
          }
        });

        // Update the sentEmails field for removed assignees
        const existingAssignees = Object.keys(taskData.sentEmails || {});
        const removedAssignees = existingAssignees.filter(
          (assigneeId) => !selectedAssignees.includes(assigneeId)
        );
        if (removedAssignees.length > 0) {
          const updateData = {};
          removedAssignees.forEach((assigneeId) => {
            updateData[`sentEmails.${assigneeId}`] = false;
          });
          await updateDoc(taskRef, updateData);
        }
      } else {
        console.error("Task not found for id:", taskDetails.id);
      }
    } catch (error) {
      console.error("Error fetching task or board data:", error);
    }
  };

  const updateTask = async (e) => {
    e.preventDefault();
    const taskRef = doc(
      db,
      `projects/${activeProject.id}/boards/${boardId}/tasks`,
      taskDetails.id
    );

    // Add assignees to subtasks collection
    const subtasksRef = collection(taskRef, "subtasks");
    selectedAssignees.forEach(async (assigneeId) => {
      await setDoc(doc(subtasksRef), {
        assigneeId: assigneeId,
        completed: false, // Assuming subtasks start as not completed
      });
    });

    await updateDoc(taskRef, {
      title: updatedTitle,
      priority: updatedPriority,
      description: updatedDesc,
      dueDate: dueDate,
      assignees: selectedAssignees, // Update assignees
    });
    sendEmail(); // Call sendEmail function here
    closeModal();
  };

  const handleDueDateChange = async (date) => {
    setDueDate(date);

    try {
      const taskRef = doc(
        db,
        `projects/${activeProject.id}/boards/${boardId}/tasks`,
        taskDetails.id
      );
      await updateDoc(taskRef, {
        dueDate: date,
      });
    } catch (error) {
      console.error("Error updating due date:", error);
    }
  };

  const deleteTask = async () => {
    setModal(false);
    closeModal();
    const columnRef = doc(
      db,
      `projects/${activeProject.id}/boards/${boardId}/columns`,
      columnDetails.id
    );
    await updateDoc(columnRef, {
      taskIds: arrayRemove(taskDetails.id),
    });
    const taskRef = doc(
      db,
      `projects/${activeProject.id}/boards/${boardId}/tasks`,
      taskDetails.id
    );
    await deleteDoc(taskRef);
  };

  return (
    <div className="md:px-12 text-sm md:text-base overflow-y-auto h-full">
      <Modal
        modal={modal}
        setModal={setModal}
        ariaText="Task Delete confirmation"
      >
        <div className="md:px-12">
          <div className="text-yellow-600 mb-2">
            <Exclaim />
          </div>
          <h2 className="text-base md:text-2xl text-gray-900">
            Are you sure you want to delete this task?
          </h2>
          <h3 className="text-red-600 text-sm md:text-xl">
            This cannot be undone.
          </h3>
          <div className="my-8 flex">
            <button
              className="border border-red-700 text-red-700 px-2 py-1 rounded-sm mr-4 text-sm md:text-base"
              onClick={deleteTask}
            >
              Yes, delete
            </button>
            <button
              className="bg-blue-800 text-gray-100 px-2 py-1 rounded-sm text-sm md:text-base"
              onClick={() => setModal(false)}
            >
              No, go back
            </button>
          </div>
        </div>
      </Modal>

      <form onSubmit={updateTask} autoComplete="off">
        <div className="flex flex-col justify-items-center items-center">
          <label
            className="text-gray-500 uppercase tracking-wide text-xs sm:text-sm block mr-[50px]"
            htmlFor="title"
          >
            Title:
          </label>
          <input
            maxLength="45"
            type="text"
            name="title"
            className="text-xl md:text-2xl block w-full outline-none"
            defaultValue={taskDetails.title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="lg:grid lg:grid-cols-8 gap-x-20 w-full">
          <div className="col-span-6 mt-12">
            <div>
              <label className="text-gray-500 uppercase tracking-wide text-xs sm:text-sm block">
                Checklist:
              </label>
              <Checklist
                todos={taskDetails.todos}
                taskId={taskDetails.id}
                boardId={boardId}
                projectId={activeProject.id}
              />
            </div>

            <div className="mt-12 w-full">
              <div className={`${editing ? "" : "hidden"}`}>
                <div className="">
                  <label
                    className="text-gray-500 uppercase tracking-wide text-xs sm:text-sm block"
                    htmlFor="desc"
                  >
                    Description:
                  </label>
                  <textarea
                    name="desc"
                    className="border border-gray-300 px-4 py-3 outline-none h-40 w-full"
                    defaultValue={taskDetails.description}
                    onChange={(e) => setNewDesc(e.target.value)}
                  />
                  <div>
                    <div
                      onClick={() => setEditing(false)}
                      className="inline-block cursor-pointer text-gray-700 px-2 py-0.5 rounded-sm bg-gray-300"
                    >
                      Cancel
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <label
                    className="text-gray-500 uppercase tracking-wide text-xs sm:text-sm block"
                    htmlFor="desc"
                  >
                    Live Preview:
                  </label>
                  <ReactMarkdown
                    remarkPlugins={[gfm]}
                    className="border border-gray-200 px-2 py-3 overflow-y-auto leading-normal prose text-sm sm:text-base leading-tight text-gray-900"
                  >
                    {updatedDesc}
                  </ReactMarkdown>
                </div>
              </div>

              <div
                className={`${editing ? "hidden" : ""}`}
                onClick={() => setEditing(true)}
              >
                <label
                  className="text-gray-500 uppercase tracking-wide text-xs sm:text-sm block"
                  htmlFor="desc"
                >
                  Description:
                </label>
                <ReactMarkdown
                  remarkPlugins={[gfm]}
                  className="border border-gray-200 bg-gray-50 px-2 py-3 overflow-y-auto prose text-sm sm:text-base leading-normal text-gray-900"
                >
                  {taskDetails.description === "" ||
                  taskDetails.description === null
                    ? "*No description yet, type here to add*"
                    : updatedDesc}
                </ReactMarkdown>
              </div>
            </div>

            {/* Assignee Selector */}
            <div className="mt-12">
              <label className="text-gray-500 uppercase tracking-wide text-xs sm:text-sm block">
                Assignees:
              </label>
              <AssigneeSelector
                assignees={users}
                selectedAssignees={selectedAssignees}
                setSelectedAssignees={setSelectedAssignees}
                creatorId={taskDetails.creator ? taskDetails.creator.uid : null}
              />
            </div>
          </div>

          <div className="col-span-2 mt-12">
            <div>
              <label
                className="text-gray-500 uppercase tracking-wide text-xs sm:text-sm block"
                htmlFor="priority"
              >
                Priority:
              </label>
              <div className="flex items-center">
                <select
                  name="priority"
                  defaultValue={taskDetails.priority}
                  className="select"
                  onChange={(e) => setPriority(e.target.value)}
                >
                  <option className="option" value="high">
                    High
                  </option>
                  <option className="option" value="medium">
                    Medium
                  </option>
                  <option className="option" value="low">
                    Low
                  </option>
                </select>
                {extractPriority(taskDetails.priority)}
              </div>
            </div>

            <div className="mt-12">
              <label
                className="text-gray-500 uppercase tracking-wide text-xs sm:text-sm block"
                htmlFor="status"
              >
                Status:
              </label>
              <h4 className="bg-gray-600 rounded-sm text-white px-2 py-1 inline-block">
                {columnDetails.title}
              </h4>
            </div>

            {taskDetails.dateAdded ? (
              <div className="mt-12">
                <label
                  className="text-gray-500 uppercase tracking-wide text-xs sm:text-sm block"
                  htmlFor="dateAdded"
                >
                  Date Added:
                </label>
                <h4 className="tracking-wide">
                  {
                    new Date(taskDetails.dateAdded.seconds * 1000)
                      .toLocaleString()
                      .split(",")[0]
                  }
                </h4>
              </div>
            ) : null}

            <div className="mt-12">
              <label
                className="text-gray-500 uppercase tracking-wide text-xs sm:text-sm block"
                htmlFor="dueDate"
              >
                Due Date:
              </label>
              <DatePicker
                selected={dueDate}
                onChange={(date) => handleDueDateChange(date)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholderText="Select a due date"
                dateFormat="MMMM d, yyyy"
              />
            </div>
          </div>
        </div>
        {/* Assignees Display */}
        <div className="mt-10">
          <label className="text-gray-500 uppercase tracking-wide text-xs sm:text-sm block">
            Task Assignees:
          </label>
          <div className="flex flex-wrap gap-4 mt-2">
            {selectedAssignees.map((assigneeId) => {
              const assignee = users.find((user) => user.id === assigneeId);
              return assignee ? (
                <div key={assignee.id} className="flex items-center gap-2">
                  <img
                    src={
                      assignee.profileImage || "https://via.placeholder.com/40"
                    }
                    alt={assignee.displayName}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <span>{assignee.displayName}</span>
                </div>
              ) : null;
            })}
          </div>
        </div>

        <div className="my-12 flex justify-end w-full text-sm sm:text-base">
          {taskDetails.description !== updatedDesc ||
          taskDetails.title !== updatedTitle ||
          taskDetails.priority !== updatedPriority ||
          taskDetails.dueDate?.toDate() !== dueDate ||
          JSON.stringify(taskDetails.assignees) !==
            JSON.stringify(selectedAssignees) ? (
            <div className="bg-green-700 text-white px-2 py-1 rounded-sm transform hover:-translate-y-1 transition-transform duration-300">
              <button className="cursor-pointer" type="submit">
                Save changes
              </button>
            </div>
          ) : null}

          <div
            className="border border-red-700 text-red-700 hover:bg-red-700 hover:text-white transition-colors duration-300 px-2 py-0.5 rounded-sm ml-4"
            onClick={() => setModal(true)}
          >
            <p className="cursor-pointer">Delete Task</p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default TaskDetails;
